import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, Platform, View, StyleSheet } from 'react-native';
import { YStack } from '@tamagui/stacks';
import { MapPin, Star } from 'lucide-react-native';
import { COLORS } from '@/constants/colors';
import type { SwipeCardProps } from './types';
import InfoBadge from './InfoBadge';
import PhotoIndicator from './PhotoIndicator';
import ProfileInfo from './ProfileInfo';
import AuthenticatedImage from '@/components/AuthenticatedImage';
import {
  BADGE_SPACING,
  BADGE_ICON_SIZE,
} from './constants';

const SwipeCard = React.memo<SwipeCardProps>(
  ({ profile, onInfoPress, announcement }) => {
    const [photoIndex, setPhotoIndex] = useState(0);

    useEffect(() => {
      setPhotoIndex(0);
    }, [profile.id]);

    // Use photos from /meeting-announcements/:id/photos endpoint (stored in profile.photos)
    // Fallback to profile.photoUrl (which may be createdByPhotoThumbnail or placeholder)
    const photos = useMemo(() => {
      if (profile.photos && profile.photos.length > 0) {
        return profile.photos;
      }
      return [profile.photoUrl];
    }, [profile.photos, profile.photoUrl]);

    const displayPhotoUrl = photos[photoIndex];

    const handleInfoPress = useCallback(() => {
      if (onInfoPress) {
        onInfoPress();
      }
    }, [onInfoPress]);

    const handlePrevPhoto = useCallback(() => {
      if (photos.length <= 1) return;
      setPhotoIndex(prev => (prev - 1 + photos.length) % photos.length);
    }, [photos.length]);

    const handleNextPhoto = useCallback(() => {
      if (photos.length <= 1) return;
      setPhotoIndex(prev => (prev + 1) % photos.length);
    }, [photos.length]);

    // Use View wrapper for web to ensure proper sizing
    const Container = Platform.OS === 'web' ? View : YStack;
    const containerProps =
      Platform.OS === 'web'
        ? { style: styles.webContainer }
        : { alignSelf: 'center' as const, flex: 1 };

    return (
      <Container {...containerProps}>
        <YStack
          borderRadius="$4"
          overflow="hidden"
          backgroundColor="$background"
          flex={1}
        >
          <YStack position="relative" flex={1}>
            <AuthenticatedImage
              uri={displayPhotoUrl}
              style={
                Platform.OS === 'web' ? styles.webImage : styles.nativeImage
              }
              resizeMode="cover"
            />

            {/* Distance Badge */}
            <YStack
              position="absolute"
              top={BADGE_SPACING}
              left={BADGE_SPACING}
            >
              <InfoBadge
                icon={
                  <MapPin
                    size={BADGE_ICON_SIZE}
                    color={COLORS.ZINC_900}
                    fill={COLORS.ZINC_900}
                  />
                }
                text={profile.distanceKm ?? '1.5 km'}
              />
            </YStack>

            {/* Rating Badge */}
            <YStack
              position="absolute"
              top={BADGE_SPACING}
              right={BADGE_SPACING}
            >
              <InfoBadge
                icon={
                  <Star
                    size={BADGE_ICON_SIZE}
                    color={COLORS.GOLD}
                    fill={COLORS.GOLD}
                  />
                }
                text={profile.rating ?? '4.9'}
              />
            </YStack>


            {/* Photo Indicator - centered between top and ProfileInfo */}
            <YStack
              position="absolute"
              right={BADGE_SPACING}
              top={0}
              bottom="30%"
              justifyContent="center"
            >
              <PhotoIndicator
                photoCount={photos.length}
                currentIndex={photoIndex}
              />
            </YStack>

            {/* Profile Info Section */}
            <ProfileInfo profile={profile} onInfoPress={handleInfoPress} />

            {/* Photo Navigation Areas */}
            <Pressable onPress={handlePrevPhoto} style={styles.navAreaLeft} />
            <Pressable onPress={handleNextPhoto} style={styles.navAreaRight} />
          </YStack>
        </YStack>
      </Container>
    );
  },
);

const styles = StyleSheet.create({
  webContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 500,
    alignSelf: 'center',
    minHeight: 500,
  },
  webImage: {
    width: '100%',
    height: '100%',
    backgroundColor: COLORS.ZINC_200,
  },
  nativeImage: {
    width: '100%',
    height: '100%',
    backgroundColor: COLORS.ZINC_200,
  },
  navAreaLeft: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: '35%', // Don't overlap with ProfileInfo
    width: '50%',
  },
  navAreaRight: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: '35%', // Don't overlap with ProfileInfo
    width: '50%',
  },
});

SwipeCard.displayName = 'SwipeCard';

export default SwipeCard;
